import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { Check, ChevronLeft, Loader2, Send } from 'lucide-react';
import { isAxiosError } from 'axios';
import { toast } from 'sonner';
import axiosInstance from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { EventsPageShell } from '../components/EventsPageShell';
import { MarketingReportCard } from '@/components/events/MarketingReportCard';
import {
  readUpdateAudience,
  storeMarketingReport,
  storeUpdateAudience,
  type MarketingBatch,
} from '@/lib/marketing-report';

type Audience = 'REGISTERED' | 'UNREGISTERED' | 'ALL_MEMBERS';

type MarketingResponse = {
  batches: MarketingBatch[];
};

function isAudience(value: string | null | undefined): value is Audience {
  return value === 'REGISTERED' || value === 'UNREGISTERED' || value === 'ALL_MEMBERS';
}

const AUDIENCES: { value: Audience; label: string }[] = [
  { value: 'REGISTERED', label: 'Registered' },
  { value: 'UNREGISTERED', label: 'Unregistered' },
  { value: 'ALL_MEMBERS', label: 'All members' },
];

function batchFromSend(
  audience: Audience,
  marketing: {
    queued: number;
    templateMissing: boolean;
    batchId: string | null;
    reason: string;
    snapshot: { eventTitle: string; eventStartDate: string; eventLocation: string } | null;
  }
): MarketingBatch {
  return {
    batchId: marketing.batchId || 'pending',
    reason: marketing.templateMissing
      ? 'No template is configured for this purpose.'
      : marketing.reason,
    audience,
    eventTitle: marketing.snapshot?.eventTitle || '',
    eventStartDate: marketing.snapshot?.eventStartDate || '',
    eventLocation: marketing.snapshot?.eventLocation || '',
    messageType: '',
    sent: 0,
    failed: 0,
    latestAt: new Date().toISOString(),
    queued: marketing.queued,
    templateMissing: marketing.templateMissing,
  };
}

function SendToMeButton({
  busy,
  disabled,
  onClick,
}: {
  busy: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled || busy}
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
      Send to me
    </button>
  );
}

function SendAction({
  label,
  busy,
  done,
  disabled,
  onClick,
}: {
  label: string;
  busy: boolean;
  done: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled || busy}
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
        done
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
          : 'border-primary/30 bg-primary/10 text-primary hover:bg-primary hover:text-white'
      }`}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : done ? (
        <Check className="h-4 w-4" />
      ) : (
        <Send className="h-4 w-4" />
      )}
      {busy ? 'Sending' : done ? 'Sent' : label}
    </button>
  );
}

export default function EventMarketingPage() {
  const { communityId, eventId } = useParams();
  const [inviteAudience, setInviteAudience] = useState<Audience>('ALL_MEMBERS');
  const [updateAudience, setUpdateAudience] = useState<Audience | null>(() => {
    if (!eventId) return null;
    const stored = readUpdateAudience(eventId);
    return isAudience(stored) ? stored : null;
  });
  const [sending, setSending] = useState<string | null>(null);
  const [sentKind, setSentKind] = useState<string | null>(null);
  const [activeBatch, setActiveBatch] = useState<MarketingBatch | null>(null);

  const { data, isLoading, mutate } = useSWR<MarketingResponse>(
    communityId && eventId ? `/api/communities/${communityId}/events/${eventId}/marketing` : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data)
  );

  useEffect(() => {
    if (updateAudience || !eventId) return;
    const latest = data?.batches.find(
      (batch) => batch.messageType === 'EVENT_UPDATE' && isAudience(batch.audience)
    );
    if (!latest || !isAudience(latest.audience)) return;
    setUpdateAudience(latest.audience);
    storeUpdateAudience(eventId, latest.audience);
  }, [data, eventId, updateAudience]);

  const send = async (
    kind: 'EVENT_INVITE' | 'REGISTRATION_REMINDER' | 'EVENT_UPDATE',
    audience: Audience
  ) => {
    if (!communityId || !eventId) return;
    setSending(kind);
    setSentKind(null);
    try {
      const { data: result } = await axiosInstance.post(
        `/api/communities/${communityId}/events/${eventId}/marketing`,
        { action: 'send', kind, audience }
      );
      const batch = batchFromSend(audience, result.marketing);
      setActiveBatch(batch);
      storeMarketingReport(eventId, batch);
      if (batch.templateMissing) {
        toast.error('No WhatsApp template is configured for this message.');
      } else {
        toast.success(`Queued ${batch.queued ?? 0} messages.`);
      }
      setSentKind(kind);
      void mutate();
    } catch {
      toast.error('Could not send the message.');
    } finally {
      setSending(null);
    }
  };

  const sendToMe = async (kind: 'EVENT_INVITE' | 'REGISTRATION_REMINDER' | 'EVENT_UPDATE') => {
    if (!communityId || !eventId) return;
    setSending(`preview:${kind}`);
    try {
      const { data: result } = await axiosInstance.post(
        `/api/communities/${communityId}/events/${eventId}/marketing`,
        { action: 'preview', kind }
      );
      if (result.templateMissing) {
        toast.error('No WhatsApp template is configured for this message.');
      } else {
        toast.success('Sent to your phone.');
      }
    } catch (error) {
      const message = isAxiosError(error) ? error.response?.data?.error : null;
      toast.error(typeof message === 'string' ? message : 'Could not send the preview.');
    } finally {
      setSending(null);
    }
  };

  const chooseUpdateAudience = (audience: Audience) => {
    setUpdateAudience(audience);
    if (eventId) storeUpdateAudience(eventId, audience);
  };

  return (
    <EventsPageShell>
      <div className="space-y-6 px-4 py-6 md:px-0">
        <Link
          to={`/${communityId}/events/${eventId}/marketing`}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-primary"
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          Back to marketing
        </Link>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Marketing</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            WhatsApp templates are assigned in the admin panel. People without a phone are skipped.
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            <section className="rounded-2xl bg-card p-5 shadow-sm">
              <h2 className="text-base font-semibold">Send Event Info</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Choose who should receive the event info.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {AUDIENCES.map((option) => (
                  <Button
                    key={option.value}
                    type="button"
                    variant={inviteAudience === option.value ? 'default' : 'outline'}
                    onClick={() => setInviteAudience(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <SendAction
                  label="Send info"
                  busy={sending === 'EVENT_INVITE'}
                  done={sentKind === 'EVENT_INVITE'}
                  onClick={() => void send('EVENT_INVITE', inviteAudience)}
                />
                <SendToMeButton
                  busy={sending === 'preview:EVENT_INVITE'}
                  disabled={Boolean(sending)}
                  onClick={() => void sendToMe('EVENT_INVITE')}
                />
              </div>
            </section>

            <section className="rounded-2xl bg-card p-5 shadow-sm">
              <h2 className="text-base font-semibold">Registration reminder</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Sent only to members who have not registered.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <SendAction
                  label="Send reminder"
                  busy={sending === 'REGISTRATION_REMINDER'}
                  done={sentKind === 'REGISTRATION_REMINDER'}
                  onClick={() => void send('REGISTRATION_REMINDER', 'UNREGISTERED')}
                />
                <SendToMeButton
                  busy={sending === 'preview:REGISTRATION_REMINDER'}
                  disabled={Boolean(sending)}
                  onClick={() => void sendToMe('REGISTRATION_REMINDER')}
                />
              </div>
            </section>

            <section className="rounded-2xl bg-card p-5 shadow-sm">
              <h2 className="text-base font-semibold">Event update notification</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                When the time, place, or tickets change, this group is notified.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {AUDIENCES.map((option) => (
                  <Button
                    key={option.value}
                    type="button"
                    variant={updateAudience === option.value ? 'default' : 'outline'}
                    onClick={() => chooseUpdateAudience(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <SendAction
                  label="Send now"
                  busy={sending === 'EVENT_UPDATE'}
                  done={sentKind === 'EVENT_UPDATE'}
                  disabled={!updateAudience}
                  onClick={() => {
                    if (!updateAudience) return;
                    void send('EVENT_UPDATE', updateAudience);
                  }}
                />
                <SendToMeButton
                  busy={sending === 'preview:EVENT_UPDATE'}
                  disabled={Boolean(sending)}
                  onClick={() => void sendToMe('EVENT_UPDATE')}
                />
              </div>
            </section>
          </div>
        )}
      </div>
      {activeBatch ? (
        <MarketingReportCard batch={activeBatch} onClose={() => setActiveBatch(null)} />
      ) : null}
    </EventsPageShell>
  );
}
